import { type LinkOption, LinkPickerModal, NEW_PREFIX } from '@/components/vyroba/LinkPickerModal';
import { NaskladneniZakazky } from '@/components/vyroba/NaskladneniZakazky';
import { PictureField } from '@/components/vyroba/PictureField';
import { VyskladneniLatky } from '@/components/vyroba/VyskladneniLatky';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { Fragment, type ReactNode, useEffect, useRef, useState } from 'react';
import { ColorField } from './ColorField';

type FormField = {
  name: string;
  label: string;
  type: string;
  colSm: number;
  options?: string[];
  currencies?: string[];
  required?: boolean;
  readonly?: boolean;
  linkTable?: string; // schema_link editovatelné z této zrcadlené tabulky
  /** Nabídka se skládá z hodnot tohoto pole v téže tabulce (nezrcadlená tabulka). */
  linkField?: string;
};
/** Klíč nabídky propojení — tabulka, případně i pole (linkFromSelf). */
function optionsKey(f: { linkTable?: string; linkField?: string }): string {
  return `${f.linkTable ?? ''}|${f.linkField ?? ''}`;
}

/** Předvyplnění nové zakázky (např. z fronty „nastříháno"). */
export type CreatePrefill = {
  /** Hodnoty polí — typicky id produktu v propojení. */
  fields?: Record<string, unknown>;
  /** Kusy podle popisku velikosti („110/116" → 3). */
  sizes?: Record<string, number>;
};

type DetailData = {
  formFields: FormField[];
  values: Record<string, unknown>;
  nazev?: string | null;
};

// Statické třídy šířky (Tailwind musí vidět celé jméno) — colSm z Tabidoo.
const COL_SPAN: Record<number, string> = {
  1: 'sm:col-span-1',
  2: 'sm:col-span-2',
  3: 'sm:col-span-3',
  4: 'sm:col-span-4',
  5: 'sm:col-span-5',
  6: 'sm:col-span-6',
  7: 'sm:col-span-7',
  8: 'sm:col-span-8',
  9: 'sm:col-span-9',
  10: 'sm:col-span-10',
  11: 'sm:col-span-11',
  12: 'sm:col-span-12',
};

const INPUT =
  'w-full border border-[#cbd5e1] rounded-[5px] px-3 py-2 text-sm font-semibold text-text bg-white placeholder:text-text-muted placeholder:font-normal focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary';

// Read-only pole: šedé pozadí + černý text (bez bg-white, ať se barvy nepřebíjí).
const INPUT_RO =
  'w-full border border-[#cbd5e1] rounded-[5px] px-3 py-2 text-sm font-semibold text-text bg-bg cursor-default focus:outline-none';

// Číselný input bez šipek nahoru/dolů (spinnerů).
const NO_SPIN =
  '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

// Úzký input pro množství u velikostí (bez w-full, ať se nepřebíjí s INPUT).
const SIZE_INPUT =
  'w-36 text-center border border-[#cbd5e1] rounded-[5px] px-2 py-2 text-sm font-semibold text-text bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary';

/** Hezčí select — vlastní šipka místo OS defaultu, tmavý text. */
function StyledSelect({
  value,
  onChange,
  children,
}: {
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
}) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${INPUT} appearance-none pr-9 cursor-pointer`}
      >
        {children}
      </select>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </div>
  );
}

function asMoney(v: unknown): { amount: number | null; currency: string; date: unknown } {
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    return {
      amount: typeof o.amount === 'number' ? o.amount : null,
      currency: typeof o.currency === 'string' ? o.currency : '',
      date: o.date ?? null,
    };
  }
  return { amount: typeof v === 'number' ? v : null, currency: '', date: null };
}

/** Tabidoo id navázaného záznamu (schema_link) — pro hodnotu selectu. */
function currentLinkId(v: unknown): string {
  const one = (r: unknown): string =>
    r && typeof r === 'object' && typeof (r as Record<string, unknown>).id === 'string'
      ? ((r as Record<string, unknown>).id as string)
      : '';
  if (Array.isArray(v)) return one(v[0]);
  return one(v);
}

/** Název navázaného záznamu (schema_link) z jeho polí. */
function linkLabel(v: unknown): string {
  const one = (rec: unknown): string => {
    if (!rec || typeof rec !== 'object') return '';
    const o = rec as Record<string, unknown>;
    const fields = (o.fields && typeof o.fields === 'object' ? o.fields : o) as Record<
      string,
      unknown
    >;
    for (const [k, val] of Object.entries(fields)) {
      if (typeof val === 'string' && val.trim() && /nazev|n[áa]zev|name|title|barva/i.test(k)) {
        return val.trim();
      }
    }
    for (const val of Object.values(fields)) {
      if (typeof val === 'string' && val.trim()) return val.trim();
    }
    return '';
  };
  if (Array.isArray(v)) return v.map(one).filter(Boolean).join(', ');
  return one(v);
}

/** Konkrétní pole z navázaného záznamu (schema_link) — např. `kod` u produktu. */
function linkedFieldText(v: unknown, field: string): string {
  const one = (rec: unknown): string => {
    if (!rec || typeof rec !== 'object') return '';
    const o = rec as Record<string, unknown>;
    const fields = (o.fields && typeof o.fields === 'object' ? o.fields : o) as Record<
      string,
      unknown
    >;
    const val = fields[field];
    return typeof val === 'string' ? val.trim() : typeof val === 'number' ? String(val) : '';
  };
  if (Array.isArray(v)) return one(v[0]);
  return one(v);
}

/**
 * Doplní číslo právě založené zakázky k pohybům skladu, které při vyskladnění
 * ještě žádné neměly. Selhání je nekritické — látka je odečtená správně, jen by
 * v historii chyběla vazba na zakázku.
 */
async function attachOrderToMovements(
  table: string,
  recordId: string,
  movementIds: number[],
): Promise<void> {
  try {
    const detail = await apiFetch<{ values: Record<string, unknown> }>(
      `/api/vyroba/tabidoo/${table}/${recordId}`,
    );
    const orderRef = readonlyText(detail.values.ID_zakazky).trim();
    if (!orderRef) return;
    await apiFetch('/api/sklad/movements/attach-order', {
      method: 'POST',
      body: { movement_ids: movementIds, order_ref: orderRef },
    });
  } catch {
    /* vazba na zakázku je doplňková informace, kvůli ní nerušíme uložení */
  }
}

// Pravidla pro uložení — zrcadlo toho, co hlídá backend (registr tabulek).
// Držíme je i tady, ať se chyba ukáže hned u pole a bez odeslání formuláře.
const REQUIRED_FIELDS: Record<string, string[]> = {
  vyroba: ['latka', 'naplet', 'cisloLatky', 'cisloNapletu'],
  chybovost: ['datum', 'svadlenkaSelectbox', 'mnozstvi'],
};
const REQUIRE_QTY = new Set(['vyroba']);

/**
 * Nechá jen ty chyby, které pořád platí. Nové se nepřidávají — uživatel má
 * dostat červené pole až když se pokusí uložit, ne během psaní.
 */
function pickKnown(
  shown: Record<string, string>,
  current: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const name of Object.keys(shown)) {
    if (current[name]) out[name] = current[name];
  }
  return out;
}

/** Klíč pole → poznámka k doplnění. Prázdný objekt = vše v pořádku. */
function validateForm(
  table: string,
  form: Record<string, unknown>,
): { fields: Record<string, string>; qty: string } {
  const fields: Record<string, string> = {};
  for (const name of REQUIRED_FIELDS[table] ?? []) {
    const v = form[name];
    const filled = typeof v === 'number' ? true : typeof v === 'string' ? v.trim() !== '' : !!v;
    if (!filled) fields[name] = '— je potřeba vyplnit';
  }
  let qty = '';
  if (REQUIRE_QTY.has(table)) {
    let total = 0;
    for (const [k, v] of Object.entries(form)) {
      if (!/^vel\d+_value$/.test(k)) continue;
      const n = typeof v === 'number' ? v : Number.parseInt(String(v ?? ''), 10);
      if (Number.isFinite(n)) total += n;
    }
    if (total <= 0) qty = '— zadej aspoň 1 ks u některé velikosti';
  }
  return { fields, qty };
}

function readonlyText(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if (typeof o.count === 'number') return String(o.count);
    if (typeof o.amount === 'number') return String(o.amount);
    return '';
  }
  return String(v);
}

// Aktuální lokální čas ve formátu pro <input> (bez timezone posunu).
function nowLocalDatetime(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

/**
 * Časy se ukládají v UTC (tak je drží i Tabidoo), ale dílna je zadává i čte
 * v našem čase — proto se do políčka převádějí tam a zpět. Bez toho ukazoval
 * formulář start šití o dvě hodiny dřív, než se reálně začalo.
 */
function utcToLocalInput(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso.slice(0, 19);
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

/** Opak `utcToLocalInput` — hodnota z políčka zpátky na uložený UTC tvar. */
function localInputToUtc(local: string): string | null {
  const ms = Date.parse(local);
  if (Number.isNaN(ms)) return null;
  return `${new Date(ms).toISOString().slice(0, 19)}.000Z`;
}

// Pole datum+čas (do sekund) a tlačítka „teď" → které pole vyplní aktuálním časem.
const FULL_DATETIME = new Set(['startSiti', 'konecSiti', 'datumUsitiTEST']);
// Tlačítka „teď" zároveň posouvají stav zakázky — ať to švadlenka ani
// kolegyně nemusí překlikávat ručně a stav vždycky odpovídá časům.
const NOW_BUTTON: Record<string, { target: string; dateOnly?: boolean; stav?: string }> = {
  tlactikoTedStart: { target: 'startSiti', stav: 'Šije se' },
  tlacitkoKonecTed: { target: 'konecSiti', stav: 'Došito' },
  ted: { target: 'datumUsitiTEST', stav: 'Hotovo' },
};

// Zbytek checkbox → do kterého textového pole zapsat „ZBYTEK" (prázdno při odškrtnutí).
const ZBYTEK_TARGET: Record<string, string> = {
  zbytekLatky: 'cisloLatky',
  zbytekNapletu: 'cisloNapletu',
};

/**
 * Detail / editace záznamu z libovolné zrcadlené Tabidoo tabulky — rozvržení
 * věrně z Tabidoo (šířka polí dle colSm), pole podle typu. „Uložit" zapíše
 * změny zpět do Tabidoo. `table` je klíč tabulky (viz registr v api/routes/vyroba).
 */
export function TabidooDetailModal({
  table,
  recordId,
  createTitle,
  prefill,
  onClose,
  onSaved,
  onSavedAndPrint,
}: {
  table: string;
  recordId?: string;
  createTitle?: string;
  /** Předvyplnění při zakládání — produkt a kusy z fronty střihárny. */
  prefill?: CreatePrefill;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
  /** Když je k dispozici, přibude tlačítko „Uložit a tisknout" (A6 list). */
  onSavedAndPrint?: (id: string) => void;
}) {
  useEscapeKey(onClose);
  const isCreate = !recordId;
  const [data, setData] = useState<DetailData | null>(null);
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [linkOptions, setLinkOptions] = useState<Record<string, LinkOption[]>>({});
  const [pickerField, setPickerField] = useState<FormField | null>(null);
  const [dynamicSizes, setDynamicSizes] = useState<{ n: number; label: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Chybějící povinná pole — popisek u pole zčervená a dostane poznámku.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [qtyError, setQtyError] = useState('');
  // Naskladnění zakázky (tlačítko „Naskladnit produkty").
  const [naskladneniOpen, setNaskladneniOpen] = useState(false);
  // Pohyby skladu udělané při zakládání zakázky — po uložení k nim doplníme
  // číslo zakázky, které v tu chvíli ještě neexistovalo.
  const pendingMovements = useRef<number[]>([]);

  useEffect(() => {
    let cancelled = false;
    const url = recordId
      ? `/api/vyroba/tabidoo/${table}/${recordId}`
      : `/api/vyroba/tabidoo/${table}/new`;
    apiFetch<DetailData>(url)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        // Editovatelná propojení drží v formu jen id navázaného záznamu (pro select).
        const init: Record<string, unknown> = { ...d.values };
        for (const f of d.formFields) {
          if (f.type === 'schema_link' && f.linkTable)
            init[f.name] = currentLinkId(d.values[f.name]);
        }
        // Předvyplnění z fronty „nastříháno" — produkt vybereme rovnou.
        if (isCreate && prefill?.fields) Object.assign(init, prefill.fields);
        setForm(init);
        // Načti nabídku pro každou zrcadlenou tabulku, na kterou propojení vede.
        // Klíč nabídky je tabulka + pole — jedna tabulka může mít víc propojení
        // poskládaných z vlastních hodnot (viz linkFromSelf na backendu).
        const zdroje = new Map<string, { table: string; field?: string }>();
        for (const f of d.formFields) {
          if (!f.linkTable) continue;
          zdroje.set(optionsKey(f), { table: f.linkTable, field: f.linkField });
        }
        for (const [key, z] of zdroje) {
          const url = z.field
            ? `/api/vyroba/tabidoo/${z.table}/options?field=${encodeURIComponent(z.field)}`
            : `/api/vyroba/tabidoo/${z.table}/options`;
          apiFetch<{ options: LinkOption[] }>(url)
            .then((r) => {
              if (!cancelled) setLinkOptions((p) => ({ ...p, [key]: r.options }));
            })
            .catch(() => {
              /* nabídka nekritická */
            });
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(e instanceof ApiError ? e.message : 'Chyba načítání detailu');
      });
    return () => {
      cancelled = true;
    };
  }, [table, recordId]);

  // Produktové pole (odkaz na produkty) — jeho výběr v create řídí velikosti.
  const productField = data?.formFields.find((f) => f.linkTable === 'produkty');
  const productValue =
    productField && typeof form[productField.name] === 'string'
      ? (form[productField.name] as string)
      : '';

  // V režimu zakládání dohledej velikosti vybraného produktu (produkt→kategorie).
  useEffect(() => {
    if (!isCreate || !productValue) {
      setDynamicSizes([]);
      return;
    }
    let cancelled = false;
    apiFetch<{ sizes: { n: number; label: string }[] }>(
      `/api/vyroba/produkt-velikosti/${productValue}`,
    )
      .then((r) => {
        if (!cancelled) setDynamicSizes(r.sizes);
      })
      .catch(() => {
        if (!cancelled) setDynamicSizes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isCreate, productValue]);

  // Velikosti se dotahují až podle vybraného produktu — kusy z fronty proto
  // doplníme, jakmile jsou popisky známé.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reagujeme na načtení velikostí
  useEffect(() => {
    const sizes = prefill?.sizes;
    if (!isCreate || !sizes || dynamicSizes.length === 0) return;
    setForm((p) => {
      const next = { ...p };
      for (const { n, label } of dynamicSizes) {
        const qty = sizes[label];
        if (typeof qty === 'number' && qty > 0) next[`vel${n}_value`] = qty;
      }
      return next;
    });
  }, [dynamicSizes, isCreate]);

  function set(name: string, value: unknown) {
    setForm((p) => {
      const next = { ...p, [name]: value };
      // Vyplněné pole přestane svítit červeně hned, ne až při dalším uložení.
      const problems = validateForm(table, next);
      setFieldErrors((prev) =>
        Object.keys(prev).length === 0 ? prev : pickKnown(prev, problems.fields),
      );
      setQtyError((prev) => (prev ? problems.qty : prev));
      return next;
    });
  }

  /** `andPrint` = po uložení otevřít A6 výrobní list nad uloženým záznamem. */
  async function save(andPrint = false) {
    if (!data) return;
    // Validace: Start šití musí být aspoň o vteřinu dřív než Konec šití (když oba).
    const start = typeof form.startSiti === 'string' ? form.startSiti : '';
    const konec = typeof form.konecSiti === 'string' ? form.konecSiti : '';
    if (start && konec) {
      const ts = new Date(start).getTime();
      const tk = new Date(konec).getTime();
      if (!Number.isNaN(ts) && !Number.isNaN(tk) && ts >= tk) {
        setErr('„Start šití" musí být aspoň o 1 vteřinu dřív než „Konec šití".');
        return;
      }
    }
    // Povinná pole a množství — stejná pravidla hlídá i backend. Chyba se
    // ukáže u konkrétního pole, ne jako jedna hláška nahoře.
    const problems = validateForm(table, form);
    setFieldErrors(problems.fields);
    setQtyError(problems.qty);
    if (Object.keys(problems.fields).length > 0 || problems.qty) return;
    // Create: pošli všechna vyplněná editovatelná pole. Edit: jen změny.
    const fields: Record<string, unknown> = {};
    for (const f of data.formFields) {
      if (f.readonly) continue;
      if (f.type === 'schema_link' && f.linkTable) {
        const after = typeof form[f.name] === 'string' ? (form[f.name] as string) : '';
        if (isCreate) {
          if (after) fields[f.name] = after;
        } else {
          const before = currentLinkId(data.values[f.name]);
          if (before !== after) fields[f.name] = after || null;
        }
        continue;
      }
      const val = form[f.name];
      if (isCreate) {
        if (val !== null && val !== undefined && val !== '') fields[f.name] = val;
      } else {
        const before = JSON.stringify(data.values[f.name] ?? null);
        const after = JSON.stringify(val ?? null);
        if (before !== after) fields[f.name] = val ?? null;
      }
    }
    // Beze změn není co ukládat — u „a tisknout" ale rovnou skoč na tisk.
    if (!isCreate && Object.keys(fields).length === 0) {
      if (andPrint && recordId) onSavedAndPrint?.(recordId);
      onClose();
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const res = await apiFetch<{ ok: boolean; id?: string }>(
        isCreate ? `/api/vyroba/tabidoo/${table}` : `/api/vyroba/tabidoo/${table}/${recordId}`,
        {
          method: isCreate ? 'POST' : 'PATCH',
          body: { fields },
        },
      );
      const savedId = isCreate ? (res.id ?? '') : (recordId ?? '');
      // Látka vyskladněná ještě před uložením nemá v historii číslo zakázky —
      // teď už ho známe, tak ho k těm pohybům doplníme.
      if (isCreate && savedId && pendingMovements.current.length > 0) {
        await attachOrderToMovements(table, savedId, pendingMovements.current);
        pendingMovements.current = [];
      }
      // Počkej na refresh tabulky, ať má tisk načtený uložený řádek.
      await onSaved();
      if (andPrint && savedId) onSavedAndPrint?.(savedId);
      onClose();
    } catch (e: unknown) {
      setErr(
        e instanceof ApiError ? e.describe() : isCreate ? 'Založení selhalo' : 'Uložení selhalo',
      );
      setSaving(false);
    }
  }

  // Číslo zakázky (jen tabulka „vyroba") před název produktu — ať je hned
  // vidět, o kterou zakázku jde, ne jen co se v ní šije.
  const idZakazky = data?.values.ID_zakazky;
  const idZakazkyStr =
    typeof idZakazky === 'string'
      ? idZakazky.trim()
      : typeof idZakazky === 'number'
        ? String(idZakazky)
        : '';
  const title = isCreate
    ? (createTitle ?? 'Nový záznam')
    : data?.nazev?.trim()
      ? idZakazkyStr
        ? `${idZakazkyStr} - ${data.nazev}`
        : data.nazev
      : 'Detail záznamu';

  // Velikostní pole (vel{N}_value + vel{N}_label) renderujeme jako spárovanou
  // sekci „Velikosti" + náhled produktu (image1) vpravo — jako v Tabidoo.
  const sizeValueFields = (data?.formFields ?? [])
    .filter((f) => /^vel\d+_value$/i.test(f.name))
    .map((f) => ({ n: Number(f.name.match(/\d+/)?.[0] ?? 0), field: f }))
    .sort((a, b) => a.n - b.n);
  const sizeAnchor = sizeValueFields[0]?.field.name;
  const sizeFieldNames = new Set<string>();
  for (const { field } of sizeValueFields) sizeFieldNames.add(field.name);
  for (const f of data?.formFields ?? []) {
    if (/^vel\d+_label$/i.test(f.name) || f.label.includes('Velikosti')) {
      sizeFieldNames.add(f.name);
    }
  }

  // Pruh „šití": Start šití + Start teč + Konec šití + Konec teč pod jedním
  // světle šedým pozadím (patří k sobě). Renderujeme je jako jeden blok.
  const byName = new Map((data?.formFields ?? []).map((f) => [f.name, f]));
  const SITI_GROUP = ['startSiti', 'tlactikoTedStart', 'konecSiti', 'tlacitkoKonecTed'];
  // Vyskladnění látky — u výrobní zakázky, jakmile je vybraný produkt. U nové
  // zakázky ještě není kód ani ID: látky dohledáme přes tabidoo id produktu
  // a číslo zakázky se k pohybům doplní až po uložení (viz attach-order).
  const productCode = linkedFieldText(data?.values.kodProduktu, 'kod');
  const productName =
    typeof data?.values.nazevProduktu === 'string' ? data.values.nazevProduktu : '';
  const orderRef = readonlyText(data?.values.ID_zakazky);
  const showVyskladneni = table === 'vyroba' && (!!productCode || !!productValue);
  // Naskladnit se dá jen uložená zakázka s produktem — u nové ještě není co
  // naskladnit (a nemá ani ID, na které by se zápis navázal).
  const showNaskladneni = table === 'vyroba' && !isCreate && !!recordId && !!productCode;

  // Spotřeba hlavní látky na kus (pole z Tabidoo) — místo jedenácti řádků pod
  // sebou ji ukážeme spárovanou s popisky velikostí a dopočítáme ze středu.
  const spotrebaRows =
    table === 'kategorie'
      ? Array.from({ length: 11 }, (_, i) => i + 1)
          .map((n) => ({
            field: byName.get(`spotrebaHlavniLatky${n}`),
            label: (() => {
              const lbl = form[`vel${n}_label`] ?? data?.values[`vel${n}_label`];
              return typeof lbl === 'string' ? lbl.trim() : '';
            })(),
          }))
          .filter((r): r is { field: FormField; label: string } => !!r.field && r.label !== '')
      : [];
  const spotrebaNames = new Set(
    Array.from({ length: 11 }, (_, i) => `spotrebaHlavniLatky${i + 1}`).filter((n) =>
      byName.has(n),
    ),
  );
  const spotrebaAnchor = 'spotrebaHlavniLatky1';

  // Střed a rozptyl řídí celou řadu velikostí — jakmile s nimi někdo hne,
  // přepočítáme ji hned, ať je výsledek vidět ještě před uložením.
  const spotrebaKlic = `${JSON.stringify(form.spotrebaHlavniLatkyStred ?? null)}|${
    form.rozptylSpotrebyOdStredoveVelikosti ?? ''
  }`;
  const spotrebaSeen = useRef<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: přepočet řídí jen změna středu a rozptylu
  useEffect(() => {
    if (table !== 'kategorie' || !data) return;
    // První průchod jen zapamatuje načtený stav — nic nepřepisujeme.
    if (spotrebaSeen.current === null) {
      spotrebaSeen.current = spotrebaKlic;
      return;
    }
    if (spotrebaSeen.current === spotrebaKlic) return;
    spotrebaSeen.current = spotrebaKlic;
    rozpocitatSpotrebu();
  }, [spotrebaKlic, data, table]);

  /**
   * Rozpočítá spotřebu podle středové velikosti a rozptylu: nejmenší velikost
   * má střed − rozptyl, největší střed + rozptyl a mezi tím se to rozdělí
   * rovnoměrně. Zaokrouhluje na dvě desetinná místa.
   */
  function rozpocitatSpotrebu() {
    const stredRaw = form.spotrebaHlavniLatkyStred ?? data?.values.spotrebaHlavniLatkyStred;
    const stred =
      typeof stredRaw === 'number'
        ? stredRaw
        : stredRaw && typeof stredRaw === 'object'
          ? Number((stredRaw as { amount?: unknown }).amount)
          : Number.NaN;
    const rozptyl = Number(
      form.rozptylSpotrebyOdStredoveVelikosti ?? data?.values.rozptylSpotrebyOdStredoveVelikosti,
    );
    if (!Number.isFinite(stred) || stred <= 0) return;
    const r = Number.isFinite(rozptyl) ? rozptyl : 0;
    const n = spotrebaRows.length;
    spotrebaRows.forEach(({ field }, i) => {
      const podil = n > 1 ? i / (n - 1) : 0.5;
      const hodnota = stred * (1 - r + 2 * r * podil);
      set(field.name, Math.round(hodnota * 100) / 100);
    });
  }

  const sitiFields = SITI_GROUP.map((n) => byName.get(n)).filter((f): f is FormField => !!f);
  const sitiFieldNames = new Set(sitiFields.map((f) => f.name));
  const sitiAnchor = sitiFields[0]?.name;
  // Zobraz jen velikosti, které produkt reálně má (mají popisek), případně
  // vyplněný počet. Label je vel{N}_label; první má výjimku velikost1_label.
  const fieldForSize = (n: number) => sizeValueFields.find((sf) => sf.n === n)?.field;
  const sizeRows = isCreate
    ? // Create: velikosti z vybraného produktu (kategorie), namapované na vel{N}_value.
      dynamicSizes
        .map(({ n, label }) => ({ field: fieldForSize(n), label }))
        .filter((r): r is { field: FormField; label: string } => !!r.field)
    : // Edit: velikosti z uloženého záznamu (mají popisek nebo vyplněný počet).
      sizeValueFields
        .map(({ n, field }) => {
          const lbl = data?.values[`vel${n}_label`] ?? data?.values[`velikost${n}_label`];
          const label = typeof lbl === 'string' && lbl.trim() ? lbl.trim() : '';
          return { field, label };
        })
        .filter((r) => r.label !== '' || typeof form[r.field.name] === 'number');
  // Náhled: edit → image1 z Tabidoo; create → fotka vybraného produktu (z options).
  const productOption = productField
    ? (linkOptions[optionsKey(productField)] ?? []).find((o) => o.id === productValue)
    : undefined;
  const previewImage =
    (data &&
      typeof data.values.image1 === 'string' &&
      /^https?:\/\//.test(data.values.image1) &&
      data.values.image1) ||
    productOption?.image ||
    '';

  // V create se Název produktu vyplní z vybraného produktu (readonly, mění se
  // jen výběrem jiného produktu). Tabidoo si ho stejně dopočítá při uložení.
  const productLabel = productOption?.label;
  useEffect(() => {
    if (isCreate && productLabel) setForm((p) => ({ ...p, nazevProduktu: productLabel }));
  }, [isCreate, productLabel]);

  // Celkem ks = součet kusů přes velikosti (zobrazíme pod velikostmi jako text).
  const celkemKs = sizeValueFields.reduce(
    (sum, { field }) =>
      sum + (typeof form[field.name] === 'number' ? (form[field.name] as number) : 0),
    0,
  );

  // Vykreslí jedno pole (popisek + input). Sdílené pro běžný grid i pruh „šití".
  const renderField = (f: FormField) => (
    // biome-ignore lint/a11y/noLabelWithoutControl: vstup je vnořen přes komponentu FieldInput
    <label key={f.name} className={`col-span-12 block ${COL_SPAN[f.colSm] ?? 'sm:col-span-6'}`}>
      {f.type !== 'button' && (
        <span
          className={`block text-sm mb-1 ${f.type === 'checkbox' ? 'text-center' : ''} ${
            fieldErrors[f.name] ? 'text-danger' : 'text-text'
          }`}
        >
          <span className="font-semibold">{f.label}</span>
          {fieldErrors[f.name] && (
            <span className="font-normal text-xs ml-1.5">{fieldErrors[f.name]}</span>
          )}
        </span>
      )}
      <FieldInput
        field={f}
        value={form[f.name]}
        original={data?.values[f.name]}
        onChange={(v) => set(f.name, v)}
        setField={set}
        linkOptions={f.linkTable ? (linkOptions[optionsKey(f)] ?? []) : []}
        onOpenPicker={() => setPickerField(f)}
        onNaskladnit={showNaskladneni ? () => setNaskladneniOpen(true) : undefined}
      />
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onClose}
        className="fixed inset-0 bg-black/60 cursor-default"
      />
      <div className="relative w-full max-w-4xl my-6 max-h-[90vh] flex flex-col overflow-hidden bg-surface rounded-2xl shadow-modal reveal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-surface rounded-t-2xl shrink-0">
          <h2 className="text-lg font-bold text-text truncate pr-4">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zavřít"
            className="text-text-muted hover:text-text text-xl leading-none cursor-pointer shrink-0"
          >
            ✕
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1">
          {err && (
            <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
              {err}
            </div>
          )}
          {data == null ? (
            <p className="text-sm text-text-muted">Načítám…</p>
          ) : (
            <div className="grid grid-cols-12 gap-x-4 gap-y-3 items-end">
              {data.formFields.map((f) => {
                // freehtml jsou Tabidoo display widgety (Databáze látek, Náhled) — nereplikujeme.
                if (f.type === 'freehtml') return null;
                // Spotřeba látky — jeden blok spárovaný s velikostmi kategorie.
                if (spotrebaNames.has(f.name)) {
                  if (f.name !== spotrebaAnchor || spotrebaRows.length === 0) return null;
                  return (
                    <div key="__spotreba" className="col-span-12 mt-1">
                      <div className="text-sm mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-text">
                        <span className="font-semibold">🧵 Spotřeba hlavní látky (m na kus)</span>
                        <span className="text-text-muted font-normal text-xs">
                          podle toho se počítá, kolik kusů z role ušijeme
                        </span>
                        <button
                          type="button"
                          onClick={rozpocitatSpotrebu}
                          title="Rozpočítá velikosti podle středové spotřeby a rozptylu"
                          className="rounded-[5px] border border-border bg-bg px-2.5 py-1 text-xs font-semibold text-text-secondary hover:border-text-muted cursor-pointer transition-colors"
                        >
                          Rozpočítat ze středu a rozptylu
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {spotrebaRows.map(({ field, label }) => {
                          const v = form[field.name];
                          return (
                            <div key={field.name} className="flex items-center gap-2">
                              <span className="w-20 shrink-0 text-sm font-semibold text-text bg-bg border border-[#cbd5e1] rounded-[5px] px-2 py-2 text-center">
                                {label}
                              </span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={typeof v === 'number' ? v : ''}
                                onChange={(e) =>
                                  set(
                                    field.name,
                                    e.target.value === '' ? null : Number(e.target.value),
                                  )
                                }
                                className={`${SIZE_INPUT} ${NO_SPIN}`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                // Velikostní pole vykreslíme jednou jako spárovanou sekci (viz níže).
                if (sizeFieldNames.has(f.name)) {
                  if (f.name !== sizeAnchor || sizeRows.length === 0) return null;
                  return (
                    <div key="__sizes" className="col-span-12 mt-1">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <div className={`text-sm mb-2 ${qtyError ? 'text-danger' : 'text-text'}`}>
                            <span className="font-semibold">📏 Velikosti</span>
                            {qtyError && (
                              <span className="font-normal text-xs ml-1.5">{qtyError}</span>
                            )}
                          </div>
                          <div className="flex flex-col gap-1.5 max-w-xs">
                            {sizeRows.map(({ field, label }) => {
                              const v = form[field.name];
                              return (
                                <div key={field.name} className="flex items-center gap-2">
                                  <span className="w-20 shrink-0 text-sm font-semibold text-text bg-bg border border-[#cbd5e1] rounded-[5px] px-2 py-2 text-center">
                                    {label || '—'}
                                  </span>
                                  <input
                                    type="number"
                                    value={typeof v === 'number' ? v : ''}
                                    onChange={(e) =>
                                      set(
                                        field.name,
                                        e.target.value === '' ? null : Number(e.target.value),
                                      )
                                    }
                                    className={`${SIZE_INPUT} ${NO_SPIN}`}
                                  />
                                </div>
                              );
                            })}
                          </div>
                          <div className="mt-3 text-base font-bold text-text">
                            Celkem {celkemKs} ks
                          </div>
                        </div>
                        {previewImage && (
                          <div>
                            <div className="text-sm font-semibold text-text mb-2">Náhled</div>
                            <img
                              src={previewImage}
                              alt=""
                              loading="lazy"
                              className="max-h-96 rounded-lg object-contain"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }
                // Pruh „šití" (Start/Konec) vykreslíme jednou pod šedým pozadím.
                // Nad něj patří vyskladnění látky — stejně jako v původní střihárně.
                if (sitiFieldNames.has(f.name)) {
                  if (f.name !== sitiAnchor) return null;
                  return (
                    <Fragment key="__siti">
                      {showVyskladneni && (
                        <div className="col-span-12 relative overflow-hidden bg-primary-25 border border-primary-100 rounded-lg px-4 py-3 mt-1">
                          <div className="flex items-baseline gap-2 flex-wrap mb-1">
                            <h3 className="text-base font-bold text-text">Vyskladnění látky</h3>
                            <span className="text-sm text-text-secondary">
                              {productName || productCode}
                            </span>
                            {isCreate && (
                              <span className="text-xs text-text-muted">
                                — odečet proběhne hned, číslo zakázky se do historie doplní po
                                uložení
                              </span>
                            )}
                          </div>
                          <VyskladneniLatky
                            productCode={productCode}
                            productId={isCreate ? productValue : undefined}
                            orderRef={orderRef}
                            onMovement={(id) => {
                              pendingMovements.current.push(id);
                            }}
                            onRollUsed={(serial) => {
                              // Z jaké role se stříhalo, to je právě „Číslo látky".
                              set('cisloLatky', String(serial));
                            }}
                          />
                        </div>
                      )}
                      <div className="col-span-12 bg-[#e9edf3] rounded-lg px-4 py-3 mt-1">
                        <div className="grid grid-cols-12 gap-x-4 gap-y-3 items-end">
                          {sitiFields.map((sf) => renderField(sf))}
                        </div>
                      </div>
                    </Fragment>
                  );
                }
                return renderField(f);
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-border bg-surface rounded-b-2xl shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold text-text-secondary hover:text-text px-4 py-2 cursor-pointer"
          >
            Zavřít
          </button>
          {onSavedAndPrint && (
            <button
              type="button"
              onClick={() => save(true)}
              disabled={saving || data == null}
              className="bg-primary hover:bg-primary-dark disabled:opacity-50 text-white text-base font-bold rounded-[5px] px-6 py-3.5 cursor-pointer transition-colors"
            >
              🖨️ Uložit a tisknout
            </button>
          )}
          <button
            type="button"
            onClick={() => save()}
            disabled={saving || data == null}
            className="bg-action hover:bg-action-dark disabled:opacity-50 text-white text-base font-bold rounded-[5px] px-12 py-3.5 cursor-pointer transition-colors"
          >
            {saving ? 'Ukládám…' : 'Uložit'}
          </button>
        </div>
      </div>

      {naskladneniOpen && recordId && (
        <NaskladneniZakazky
          recordId={recordId}
          onClose={() => setNaskladneniOpen(false)}
          onDone={async () => {
            setNaskladneniOpen(false);
            await onSaved();
            onClose();
          }}
        />
      )}

      {pickerField?.linkTable && (
        <LinkPickerModal
          title={pickerField.label}
          options={linkOptions[optionsKey(pickerField)] ?? []}
          currentId={
            typeof form[pickerField.name] === 'string'
              ? (form[pickerField.name] as string)
              : currentLinkId(form[pickerField.name])
          }
          onPick={(id) => {
            set(pickerField.name, id);
            setPickerField(null);
          }}
          onClose={() => setPickerField(null)}
          allowNew={!!pickerField.linkField}
        />
      )}
    </div>
  );
}

function FieldInput({
  field: f,
  value,
  original,
  onChange,
  setField,
  linkOptions,
  onOpenPicker,
  onNaskladnit,
}: {
  field: FormField;
  value: unknown;
  original: unknown;
  onChange: (v: unknown) => void;
  setField: (name: string, value: unknown) => void;
  linkOptions: LinkOption[];
  onOpenPicker: () => void;
  /** Naskladnění zakázky — jen u uložené výrobní zakázky, jinak undefined. */
  onNaskladnit?: () => void;
}) {
  if (f.type === 'picture') {
    return <PictureField value={value} onChange={onChange} />;
  }

  if (f.type === 'color') {
    return <ColorField value={typeof value === 'string' ? value : ''} onChange={onChange} />;
  }

  if (f.type === 'checkbox') {
    const checked = value === true || value === 'true';
    return (
      // Vlastní checkbox (skrytý input + box + SVG fajfka) — ostrý v jakékoli
      // velikosti (nativní scale by rozmazával, Safari neškáluje přes w/h).
      <div className="flex items-center justify-center h-[38px]">
        <span className="relative inline-flex h-[22px] w-[22px] cursor-pointer">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => {
              onChange(e.target.checked);
              // Zbytek checkbox rovnou vyplní/vyprázdní příslušné „Číslo …".
              const tgt = ZBYTEK_TARGET[f.name];
              if (tgt) setField(tgt, e.target.checked ? 'ZBYTEK' : '');
            }}
            className="peer sr-only"
          />
          <span
            aria-hidden="true"
            className="h-[22px] w-[22px] rounded-[5px] border-2 border-[#cbd5e1] bg-white transition-colors peer-checked:border-primary peer-checked:bg-primary"
          />
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="pointer-events-none absolute inset-0 m-auto h-4 w-4 text-white opacity-0 peer-checked:opacity-100"
          >
            <path d="M5 13l4 4L19 7" />
          </svg>
        </span>
      </div>
    );
  }

  if (f.type === 'button') {
    // „…teď" tlačítka vyplní příslušné pole aktuálním časem; „Naskladnit
    // produkty" otevře naskladnění zakázky (u uložené zakázky). Zbylá tlačítka
    // zdědila z Tabidoo automatizaci, kterou nemáme — zůstávají neaktivní.
    const now = NOW_BUTTON[f.name];
    const naskladnit = !now && !!onNaskladnit && /naskladnit/i.test(f.label);
    const color = now
      ? 'bg-primary hover:bg-primary-dark text-white cursor-pointer'
      : naskladnit
        ? 'bg-success hover:opacity-90 text-white cursor-pointer'
        : /naskladnit/i.test(f.label)
          ? 'bg-border-light hover:bg-border text-text border border-border cursor-default'
          : 'bg-bg hover:bg-border-light text-text-secondary border border-border cursor-default';
    return (
      <button
        type="button"
        title={
          now
            ? now.stav
              ? `Vyplní aktuální čas a přepne stav na „${now.stav}"`
              : 'Vyplní aktuální čas'
            : naskladnit
              ? 'Naskladní kusy zakázky na sklad a uzavře ji'
              : 'Akce dostupná jen v Tabidoo'
        }
        onClick={
          now
            ? () => {
                setField(
                  now.target,
                  now.dateOnly
                    ? nowLocalDatetime().slice(0, 10)
                    : localInputToUtc(nowLocalDatetime()),
                );
                if (now.stav) setField('stav', now.stav);
              }
            : naskladnit
              ? onNaskladnit
              : undefined
        }
        className={`w-auto rounded-[5px] px-3 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${color}`}
      >
        {f.label}
      </button>
    );
  }

  if (f.type === 'schema_link') {
    // Propojení na zrcadlenou tabulku → editovatelný výběr; jinak jen zobrazení.
    if (f.linkTable) {
      const cur = typeof value === 'string' ? value : currentLinkId(value);
      const opt = linkOptions.find((o) => o.id === cur);
      // Nově zakládaná položka ještě nemá id — v hodnotě je „new:<název>".
      const label = cur.startsWith(NEW_PREFIX)
        ? `${cur.slice(NEW_PREFIX.length)} (nová)`
        : (opt?.label ?? linkLabel(original));
      const image = opt?.image;
      return (
        <button
          type="button"
          onClick={onOpenPicker}
          className={`${INPUT} flex items-center gap-2 text-left cursor-pointer hover:border-primary`}
        >
          {image && (
            <img
              src={image}
              alt=""
              className="h-6 w-6 shrink-0 object-contain rounded-md border border-border bg-white"
            />
          )}
          <span className={`flex-1 truncate ${label ? 'text-text' : 'text-text-muted'}`}>
            {label || '— vybrat —'}
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="shrink-0 text-text-muted"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      );
    }
    const label = linkLabel(value);
    return (
      <input
        type="text"
        value={label || '—'}
        readOnly
        title="Propojení na jinou tabulku (jen zobrazení — necílí na zrcadlenou tabulku)"
        className={INPUT_RO}
      />
    );
  }

  if (f.readonly) {
    return <input type="text" value={readonlyText(value)} readOnly className={INPUT_RO} />;
  }

  if (f.type === 'dropdown') {
    return (
      <StyledSelect value={typeof value === 'string' ? value : ''} onChange={onChange}>
        <option value="" />
        {(f.options ?? []).map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </StyledSelect>
    );
  }

  if (f.type === 'money_with_currency') {
    const m = asMoney(value);
    return (
      <div className="flex gap-1">
        <input
          type="number"
          value={m.amount ?? ''}
          onChange={(e) =>
            onChange({
              ...m,
              amount: e.target.value === '' ? null : Number(e.target.value),
              currency: m.currency || (f.currencies?.[0] ?? ''),
            })
          }
          className={`${INPUT} ${NO_SPIN} flex-1 min-w-0 text-right`}
        />
        <select
          value={m.currency || (f.currencies?.[0] ?? '')}
          onChange={(e) => onChange({ ...m, currency: e.target.value })}
          className="shrink-0 w-16 appearance-none text-center border border-[#cbd5e1] rounded-[5px] px-1 py-2 text-sm font-semibold text-text bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        >
          {(f.currencies ?? []).map((cu) => (
            <option key={cu} value={cu}>
              {cu}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (f.type === 'datetime') {
    // Start/Konec šití = datum + čas (do sekund); ostatní jen datum.
    if (FULL_DATETIME.has(f.name)) {
      const val = typeof value === 'string' ? utcToLocalInput(value) : '';
      return (
        <input
          type="datetime-local"
          step={1}
          value={val}
          onChange={(e) => onChange(localInputToUtc(e.target.value))}
          className={INPUT}
        />
      );
    }
    const iso = typeof value === 'string' ? value.slice(0, 10) : '';
    return (
      <input
        type="date"
        value={iso}
        onChange={(e) => onChange(e.target.value || null)}
        className={INPUT}
      />
    );
  }

  if (f.type === 'decimal' || f.type === 'number' || f.type === 'percentage') {
    return (
      <input
        type="number"
        value={typeof value === 'number' ? value : ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        className={`${INPUT} ${NO_SPIN} text-right`}
      />
    );
  }

  // text a ostatní
  return (
    <input
      type="text"
      value={typeof value === 'string' ? value : value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
      className={INPUT}
    />
  );
}
